/** Site-wide public contact email used across UI, SEO schema, emails, and defaults. */
export const DEFAULT_CONTACT_EMAIL = "hello@vacationrentaloahu.co";

/** Known incorrect or private emails previously stored as the public contact_email. */
const LEGACY_CONTACT_EMAILS = new Set([
  "janinaseitz75@gmail.com",
  "info@vacationrentaloahu.co",
]);

export function isLegacyContactEmail(email: string): boolean {
  return LEGACY_CONTACT_EMAILS.has(email.trim().toLowerCase().replace(/^"+|"+$/g, ""));
}

/** Canonical public contact email. Replaces missing or known-legacy values. */
export function formatContactEmail(email?: string | null): string {
  const trimmed = email?.trim().replace(/^"+|"+$/g, "");
  if (!trimmed || isLegacyContactEmail(trimmed)) {
    return DEFAULT_CONTACT_EMAIL;
  }
  return trimmed;
}

export function contactMailto(email: string = DEFAULT_CONTACT_EMAIL): string {
  return `mailto:${formatContactEmail(email)}`;
}

/** Site-wide host contact phone used across UI, SEO schema, emails, and defaults. */
export const DEFAULT_CONTACT_PHONE = "(808) 255-4348";
export const DEFAULT_CONTACT_PHONE_E164 = "+18082554348";
export const DEFAULT_CONTACT_PHONE_TEL_DISPLAY = "+1-808-255-4348";

/** Known incorrect numbers previously stored in settings / exports. */
const LEGACY_CONTACT_PHONE_DIGITS = new Set([
  "8082554338", // typo of the current number
  "2089959516", // old Idaho number from older exports
]);

export function digitsOnlyPhone(phone: string): string {
  return phone.replace(/\D/g, "");
}

export function isLegacyContactPhone(phone: string): boolean {
  return LEGACY_CONTACT_PHONE_DIGITS.has(digitsOnlyPhone(phone));
}

/** Normalize a US phone string to E.164 (+1XXXXXXXXXX). */
export function toE164Phone(phone: string = DEFAULT_CONTACT_PHONE): string {
  let digits = digitsOnlyPhone(phone);
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length > 10) {
    return `+1${digits.slice(-10)}`;
  }
  return DEFAULT_CONTACT_PHONE_E164;
}

/** Canonical display phone. Replaces missing or known-legacy values. */
export function formatContactPhone(phone?: string | null): string {
  const trimmed = phone?.trim().replace(/^"+|"+$/g, "");
  if (!trimmed || isLegacyContactPhone(trimmed)) {
    return DEFAULT_CONTACT_PHONE;
  }
  return trimmed;
}

export function contactTelHref(phone: string = DEFAULT_CONTACT_PHONE): string {
  return `tel:${toE164Phone(phone)}`;
}

export function contactSmsHref(phone: string = DEFAULT_CONTACT_PHONE): string {
  return `sms:${toE164Phone(phone)}`;
}
